import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter
} from 'reactstrap';
import Draggable, { DraggableCore } from 'react-draggable';
import Impersonate, { SortingState } from './Impersonate';
import './styles.css';

interface ImpersonationModalProps {
  isOpen: boolean;
  toggle: () => void;
  onImpersonationChange: () => void;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  sortBy: SortingState | undefined;
  setSortBy: React.Dispatch<React.SetStateAction<SortingState | undefined>>;
}

const ImpersonationModal: React.FC<ImpersonationModalProps> = ({
  isOpen,
  toggle,
  onImpersonationChange,
  searchTerm,
  setSearchTerm,
  sortBy,
  setSortBy
}) => {
  const [position, setPosition] = useState({ x: 0, y: 30 });
  const modalRef = useRef<HTMLDivElement>(null);
  const dragNodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reset position when modal is opened
    if (isOpen) {
      setPosition({ x: 0, y: 30 });
    }
  }, [isOpen]);

  const handleDrag = (e: any, data: any) => {
    setPosition({ x: data.x, y: data.y });
  };

  // Create a wrapper component that uses nodeRef to avoid findDOMNode
  const DraggableWrapper = ({ children }: { children: React.ReactNode }) => (
    <Draggable
      nodeRef={dragNodeRef}
      handle=".modal-header"
      position={position}
      onDrag={handleDrag}
      onStop={handleDrag}
      bounds="parent"
    >
      <div ref={dragNodeRef} style={{ position: 'absolute', zIndex: 1055 }}>
        {children}
      </div>
    </Draggable>
  );

  // Alternative approach without Draggable if you want to completely avoid it
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!modalRef.current) return;
    
    const modal = modalRef.current;
    const rect = modal.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - offsetX;
      const newY = e.clientY - offsetY;
      
      // Constrain to viewport
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;
      
      const constrainedX = Math.max(0, Math.min(newX, maxX));
      const constrainedY = Math.max(0, Math.min(newY, maxY));
      
      setPosition({ x: constrainedX, y: constrainedY });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <>
      {isOpen && (
        <div 
          className="modal-backdrop fade show" 
          style={{ zIndex: 1050 }}
          onClick={toggle}
        />
      )}
      
      {isOpen && (
        <DraggableWrapper>
          <div
            ref={modalRef}
            className="modal fade show d-block"
            style={{
              position: 'static',
              zIndex: 1055,
              maxWidth: '96vw',
              width: 'min(1200px, calc(100vw - 4rem))',
              margin: '0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-dialog modal-xl" style={{ margin: 0, maxWidth: '100%' }}>
              <div className="modal-content">
                <div 
                  className="modal-header"
                  style={{ 
                    cursor: 'move',
                    userSelect: 'none',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6'
                  }}
                  onMouseDown={handleMouseDown}
                >
                  <h5 className="modal-title">Impersonation</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={toggle}
                  />
                </div>
                
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <Impersonate
                    onImpersonationChange={onImpersonationChange}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                  />
                </div>
                
                <div className="modal-footer">
                  <Button color="secondary" onClick={toggle}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DraggableWrapper>
      )}
    </>
  );
};

export default ImpersonationModal;